export type VersionCopyIntent = {
  storageKey: string;
  idempotencyKey: string;
  generation: number;
};

const generationFallback = new Map<string, number>();
const pendingFallback = new Map<string, number>();

/** 与后端标识符比较保持一致，避免 UUID 大小写别名拆分复制锁与幂等代次。 */
export function canonicalVersionCopyScope(budgetOrderId: string): string {
  return budgetOrderId.toLowerCase();
}

/** 安全读取浏览器存储实例。 */
function storage(kind: "localStorage" | "sessionStorage"): Storage | null {
  try {
    return globalThis[kind] ?? null;
  } catch {
    return null;
  }
}

/** 读取非负整数代次，并区分缺值与存储不可访问。 */
function readStoredGeneration(
  target: Storage | null,
  key: string,
): { available: boolean; value: number | null } {
  try {
    if (target === null) return { available: false, value: null };
    const raw = target?.getItem(key);
    if (raw === null || raw === undefined || raw === "") {
      return { available: true, value: null };
    }
    const value = Number(raw);
    return {
      available: true,
      value: Number.isSafeInteger(value) && value >= 0 ? value : null,
    };
  } catch {
    return { available: false, value: null };
  }
}

/** 尝试写入代次，失败时由调用方的内存状态兜底。 */
function writeStoredGeneration(target: Storage | null, key: string, value: number): boolean {
  try {
    target?.setItem(key, String(value));
    return target !== null;
  } catch {
    return false;
  }
}

/** 尝试清除浏览器存储值。 */
function removeStoredValue(target: Storage | null, key: string) {
  try {
    target?.removeItem(key);
  } catch {
    // 内存兜底仍会清理当前页面内的 pending。
  }
}

/** 当前标签未完成意图的存储键。 */
function pendingKey(storageKey: string) {
  return `${storageKey}:pending`;
}

/** localStorage 不可用时的 sessionStorage 代次键。 */
function fallbackGenerationKey(storageKey: string) {
  return `${storageKey}:generation`;
}

/** 读取跨标签共享代次及当前标签兜底代次中的最大值。 */
function currentGeneration(storageKey: string): number {
  const local = readStoredGeneration(storage("localStorage"), storageKey);
  const session = readStoredGeneration(
    storage("sessionStorage"),
    fallbackGenerationKey(storageKey),
  );
  const candidates = [
    local.value,
    session.value,
    generationFallback.get(storageKey),
  ].filter((value): value is number => value !== null && value !== undefined);
  return Math.max(0, ...candidates);
}

/**
 * 创建或恢复当前标签页未完成的复制意图。
 * sessionStorage 保留响应未知的请求键，共享 localStorage 仅分配已完成意图的下一代次。
 */
export function beginVersionCopyIntent(
  storageKey: string,
  idempotencyPrefix: string,
): VersionCopyIntent {
  const pendingStorageKey = pendingKey(storageKey);
  const storedPending = readStoredGeneration(storage("sessionStorage"), pendingStorageKey);
  const pending = storedPending.value
    ?? pendingFallback.get(storageKey);
  const generation = pending ?? currentGeneration(storageKey);
  if (writeStoredGeneration(storage("sessionStorage"), pendingStorageKey, generation)) {
    pendingFallback.delete(storageKey);
  } else {
    pendingFallback.set(storageKey, generation);
  }
  return {
    storageKey,
    idempotencyKey: `${idempotencyPrefix}:${generation}`,
    generation,
  };
}

/** 仅在创建结果已确认并可导航后，结束 pending 并推进下一次显式复制意图。 */
export function completeVersionCopyIntent(intent: VersionCopyIntent) {
  const nextGeneration = Math.max(currentGeneration(intent.storageKey), intent.generation + 1);
  const localWritten = writeStoredGeneration(
    storage("localStorage"),
    intent.storageKey,
    nextGeneration,
  );
  const sessionWritten = writeStoredGeneration(
    storage("sessionStorage"),
    fallbackGenerationKey(intent.storageKey),
    nextGeneration,
  );
  if (localWritten || sessionWritten) {
    generationFallback.delete(intent.storageKey);
  } else {
    generationFallback.set(intent.storageKey, nextGeneration);
  }

  const currentPending = readStoredGeneration(
    storage("sessionStorage"),
    pendingKey(intent.storageKey),
  );
  if (currentPending.value === intent.generation) {
    removeStoredValue(storage("sessionStorage"), pendingKey(intent.storageKey));
  }
  if (pendingFallback.get(intent.storageKey) === intent.generation) {
    pendingFallback.delete(intent.storageKey);
  }
}
